import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getConnections, getInvitations, inviteAdvisor,
  acceptConnection, declineConnection, revokeConnection,
  getServiceTypes, getReferralCandidates, getReferralCandidateData, getReferrals, createReferral,
  acceptReferral, declineReferral, updateReferralStatus, cancelReferral,
} from '../services/advisorNetworkService';
import {
  getSubcontractorLinks, inviteSubcontractor, acceptSubcontractor, declineSubcontractor,
} from '../services/subcontractorService';

/**
 * Network page — manage your advisor network and user referrals.
 *  Connections: invite advisers, accept/decline, disconnect.
 *  Referrals:   refer a user to a connected partner (with consent + data scope),
 *               and track / act on referrals in both directions.
 */

// ---------- styles ----------
const Page = styled.div` height:100%; overflow-y:auto; background:#0f1419; color:#e6edf3; padding:28px 32px; `;
const Header = styled.div` display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; flex-wrap:wrap; gap:12px; `;
const Title = styled.h1` font-size:24px; margin:0; font-weight:600; `;
const Subtitle = styled.p` color:#8b98a5; margin:4px 0 24px; font-size:14px; `;
const HeaderBtns = styled.div` display:flex; gap:10px; `;
const PrimaryBtn = styled.button` background:#2f81f7; color:#fff; border:none; border-radius:8px; padding:10px 18px; font-size:14px; font-weight:600; cursor:pointer; &:hover{background:#4f9bff;} &:disabled{opacity:.5;cursor:default;} `;
const GhostBtn = styled.button` background:transparent; color:#e6edf3; border:1px solid rgba(255,255,255,0.14); border-radius:8px; padding:10px 18px; font-size:14px; font-weight:600; cursor:pointer; &:hover{background:rgba(255,255,255,0.06);} `;
const Tabs = styled.div` display:flex; gap:8px; margin-bottom:20px; `;
const Tab = styled.button` background:${p=>p.$active?'rgba(47,129,247,0.15)':'transparent'}; color:${p=>p.$active?'#4f9bff':'#8b98a5'}; border:1px solid ${p=>p.$active?'rgba(47,129,247,0.4)':'rgba(255,255,255,0.08)'}; border-radius:8px; padding:8px 16px; font-size:14px; cursor:pointer; display:flex; align-items:center; gap:8px; `;
const Badge = styled.span` background:#f85149; color:#fff; border-radius:10px; font-size:11px; padding:1px 7px; font-weight:700; `;
const Card = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:16px 18px; margin-bottom:12px; display:flex; align-items:center; justify-content:space-between; gap:16px; `;
const PartnerInfo = styled.div` display:flex; align-items:center; gap:14px; min-width:0; `;
const Avatar = styled.div` width:40px; height:40px; border-radius:50%; flex-shrink:0; background:linear-gradient(135deg,#2f81f7,#a371f7); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; `;
const Name = styled.div` font-weight:600; font-size:15px; `;
const Sub = styled.div` color:#8b98a5; font-size:13px; margin-top:2px; word-break:break-word; `;
const StatusPill = styled.span` font-size:12px; font-weight:600; padding:3px 10px; border-radius:20px; color:${p=>p.$c||'#8b98a5'}; background:${p=>p.$bg||'rgba(139,152,165,.12)'}; white-space:nowrap; `;
const Actions = styled.div` display:flex; gap:8px; flex-shrink:0; align-items:center; flex-wrap:wrap; justify-content:flex-end; `;
const SmallBtn = styled.button` border-radius:7px; padding:7px 14px; font-size:13px; font-weight:600; cursor:pointer; border:1px solid ${p=>p.$danger?'rgba(248,81,73,.4)':'rgba(255,255,255,0.12)'}; background:${p=>p.$primary?'#238636':'transparent'}; color:${p=>p.$primary?'#fff':p.$danger?'#f85149':'#e6edf3'}; &:hover{background:${p=>p.$primary?'#2ea043':'rgba(255,255,255,0.06)'};} `;
const Empty = styled.div` color:#8b98a5; text-align:center; padding:60px 20px; font-size:15px; `;
const SectionLabel = styled.div` color:#6b7684; font-size:12px; text-transform:uppercase; letter-spacing:.5px; margin:18px 0 10px; font-weight:700; `;
// Expandable partner card: a top row (avatar + identity + actions) with an optional
// details panel that reveals the connected adviser's full contact profile.
const CardRoot = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:16px 18px; margin-bottom:12px; `;
const CardTop = styled.div` display:flex; align-items:center; justify-content:space-between; gap:16px; `;
const AvatarImg = styled.img` width:40px; height:40px; border-radius:50%; object-fit:cover; flex-shrink:0; `;
const InlineMeta = styled.div` color:#8b98a5; font-size:12.5px; margin-top:4px; display:flex; gap:14px; flex-wrap:wrap; `;
const DetailsToggle = styled.button` background:none; border:none; color:#4f9bff; font-size:12px; cursor:pointer; padding:4px 0 0; display:inline-flex; align-items:center; gap:4px; &:hover{text-decoration:underline;} `;
const DetailsPanel = styled.div` margin-top:14px; padding-top:14px; border-top:1px solid rgba(255,255,255,0.08); display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:12px 24px; `;
const DetailItem = styled.div` font-size:13px; color:#c9d1d9; word-break:break-word; `;
const DetailLabel = styled.div` color:#6b7684; font-size:11px; text-transform:uppercase; letter-spacing:.4px; margin-bottom:3px; `;
const Link = styled.a` color:#4f9bff; text-decoration:none; &:hover{text-decoration:underline;} `;

const Overlay = styled.div` position:fixed; inset:0; background:rgba(0,0,0,.6); z-index:1000; display:flex; align-items:center; justify-content:center; `;
const Modal = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:24px; width:520px; max-width:94vw; max-height:90vh; overflow-y:auto; `;
const Label = styled.label` display:block; font-size:13px; color:#8b98a5; margin:14px 0 6px; `;
const Input = styled.input` width:100%; box-sizing:border-box; background:#0d1117; color:#e6edf3; border:1px solid rgba(255,255,255,0.12); border-radius:8px; padding:10px 12px; font-size:14px; `;
const Select = styled.select` width:100%; box-sizing:border-box; background:#0d1117; color:#e6edf3; border:1px solid rgba(255,255,255,0.12); border-radius:8px; padding:10px 12px; font-size:14px; `;
const Textarea = styled.textarea` width:100%; box-sizing:border-box; background:#0d1117; color:#e6edf3; min-height:64px; resize:vertical; border:1px solid rgba(255,255,255,0.12); border-radius:8px; padding:10px 12px; font-size:14px; font-family:inherit; `;
const ScopeRow = styled.label` display:flex; align-items:center; gap:10px; padding:8px 0; font-size:14px; color:#e6edf3; cursor:pointer; `;
const ModalActions = styled.div` display:flex; justify-content:flex-end; gap:10px; margin-top:22px; `;
const ErrorMsg = styled.div` color:#f85149; font-size:13px; margin-top:10px; `;

const initials = (s='') => s.trim().split(/\s+/).slice(0,2).map(w=>w[0]?.toUpperCase()).join('') || '?';
const STATUS_COLORS = {
  pending_consent:['#d29922','rgba(210,153,34,.12)'], pending_acceptance:['#d29922','rgba(210,153,34,.12)'],
  accepted:['#3fb950','rgba(63,185,80,.12)'], in_progress:['#4f9bff','rgba(47,129,247,.12)'],
  converted:['#3fb950','rgba(63,185,80,.12)'], closed:['#8b98a5','rgba(139,152,165,.12)'],
  declined:['#f85149','rgba(248,81,73,.12)'], consent_denied:['#f85149','rgba(248,81,73,.12)'], cancelled:['#8b98a5','rgba(139,152,165,.12)'],
};
const SCOPE_LABELS = {
  contact:'Name & contact details',
  summary:'Summary of their situation',
  analysis:'Full AI case analysis',
  collectedData:'Collected client data (forms/answers)',
  notes:'Your note to the partner',
  profile:'Selected key facts',
};

const DOW = [['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']];
const ensureHttp = (u='') => /^https?:\/\//i.test(u) ? u : `https://${u}`;
const formatAddress = (a) => a ? [a.line1, a.line2, a.city, a.province, a.postalCode, a.country].filter(Boolean).join(', ') : '';
// Structured working hours → list of "Mon 09:00–17:00" / "Sun Closed" lines (only days that carry data).
const formatHours = (wh) => {
  if (!wh || !wh.days) return [];
  return DOW.map(([k, lbl]) => {
    const d = wh.days[k];
    if (!d) return null;
    if (d.closed) return `${lbl} · Closed`;
    if (d.open && d.close) return `${lbl} · ${d.open}–${d.close}`;
    return null;
  }).filter(Boolean);
};

/**
 * Expandable card for a connected adviser (connections / subcontractors). Shows a compact
 * identity row (avatar, name, company, email, inline phone + location) and a "Details"
 * toggle that reveals the full contact profile. Every field renders only when present, so
 * an adviser with an empty profile degrades to just name + email.
 *  - partner:    { name, email, profile } from the enriched Network endpoints (profile may be null)
 *  - belowEmail: extra JSX under the email (e.g. invitation message)
 *  - actions:    right-side status pills + buttons
 */
function PartnerCard({ partner, fallbackName, belowEmail, actions }) {
  const [open, setOpen] = useState(false);
  const p = partner || {};
  const prof = p.profile || {};
  const name = p.name || fallbackName || 'Adviser';
  const structuredAddr = formatAddress(prof.address);
  const addr = structuredAddr || prof.location || '';
  const hours = formatHours(prof.workingHours);
  const tz = prof.workingHours?.timezone;
  const socials = Object.entries(prof.socialMedia || {}).filter(([, v]) => v);
  const hasDetails = !!(prof.phone || structuredAddr || prof.website || hours.length || prof.hoursClose || prof.about || socials.length);
  return (
    <CardRoot>
      <CardTop>
        <PartnerInfo>
          {prof.avatar
            ? <AvatarImg src={prof.avatar} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            : <Avatar>{initials(name)}</Avatar>}
          <div style={{ minWidth: 0 }}>
            <Name>{name}{prof.companyName ? <span style={{ color: '#8b98a5', fontWeight: 400 }}> · {prof.companyName}</span> : null}</Name>
            <Sub>{p.email}</Sub>
            {(prof.phone || addr) && (
              <InlineMeta>
                {prof.phone && <span>📞 {prof.phone}</span>}
                {addr && <span>📍 {addr}</span>}
              </InlineMeta>
            )}
            {belowEmail}
            {hasDetails && <DetailsToggle onClick={() => setOpen(o => !o)}>{open ? 'Hide details ▲' : 'Details ▼'}</DetailsToggle>}
          </div>
        </PartnerInfo>
        <Actions>{actions}</Actions>
      </CardTop>
      {open && hasDetails && (
        <DetailsPanel>
          {prof.phone && <DetailItem><DetailLabel>Phone</DetailLabel>{prof.phone}</DetailItem>}
          {structuredAddr && <DetailItem><DetailLabel>Address</DetailLabel>{structuredAddr}</DetailItem>}
          {prof.website && <DetailItem><DetailLabel>Website</DetailLabel><Link href={ensureHttp(prof.website)} target="_blank" rel="noreferrer">{prof.website}</Link></DetailItem>}
          {hours.length > 0
            ? <DetailItem><DetailLabel>Working hours{tz ? ` (${tz})` : ''}</DetailLabel><div style={{ lineHeight: 1.7 }}>{hours.map((h, i) => <div key={i}>{h}</div>)}</div></DetailItem>
            : prof.hoursClose ? <DetailItem><DetailLabel>Working hours</DetailLabel>{prof.hoursClose}</DetailItem> : null}
          {prof.about && <DetailItem style={{ gridColumn: '1 / -1' }}><DetailLabel>About</DetailLabel>{prof.about}</DetailItem>}
          {socials.length > 0 && (
            <DetailItem style={{ gridColumn: '1 / -1' }}>
              <DetailLabel>Social</DetailLabel>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {socials.map(([k, v]) => <Link key={k} href={ensureHttp(v)} target="_blank" rel="noreferrer" style={{ textTransform: 'capitalize' }}>{k}</Link>)}
              </div>
            </DetailItem>
          )}
        </DetailsPanel>
      )}
    </CardRoot>
  );
}

export default function NetworkPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [tab, setTab] = useState('connections');
  const [connections, setConnections] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [subLinks, setSubLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  // add-subcontractor modal
  const [showAddSub, setShowAddSub] = useState(false);
  const [subEmail, setSubEmail] = useState('');
  const [subMsg, setSubMsg] = useState('');
  const [subErr, setSubErr] = useState('');
  const [addingSub, setAddingSub] = useState(false);

  // invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteErr, setInviteErr] = useState('');

  // refer modal
  const [showRefer, setShowRefer] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  // Field-level share selection: the adviser curates exactly what crosses over.
  const EMPTY_REFER = { conversationId:'', toExpertId:'', serviceType:'', notes:'', summary:'',
    contactFields:['name','email','phone'], fields:[], inclSummary:true, inclAnalysis:false };
  const [refer, setRefer] = useState(EMPTY_REFER);
  const [candidateData, setCandidateData] = useState(null); // preview of what the selected client has
  const [referring, setReferring] = useState(false);
  const [referErr, setReferErr] = useState('');

  const token = useCallback(() => getAccessTokenSilently(), [getAccessTokenSilently]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const t = await token();
      const [conns, invites, refs, subs] = await Promise.all([getConnections(t), getInvitations(t), getReferrals(t), getSubcontractorLinks(t)]);
      setConnections(conns); setInvitations(invites); setReferrals(refs); setSubLinks(subs);
    } catch (e) { console.error('[Network] load failed', e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // auto-refresh so status changes appear without a manual reload
    return () => clearInterval(t);
  }, [load]);

  const activeConns = connections.filter(c => c.status === 'active');
  const pendingSent = connections.filter(c => c.status === 'pending' && c.direction === 'sent');

  // ---------- invite ----------
  const doInvite = async () => {
    setInviteErr('');
    if (!inviteEmail.trim()) { setInviteErr('Please enter an email address'); return; }
    try {
      setInviting(true);
      await inviteAdvisor(await token(), { email: inviteEmail.trim(), message: inviteMsg.trim() });
      setShowInvite(false); setInviteEmail(''); setInviteMsg(''); await load();
    } catch (e) { setInviteErr(e?.response?.data?.error || 'Failed to send invitation'); }
    finally { setInviting(false); }
  };

  // ---------- add subcontractor ----------
  const doAddSub = async () => {
    setSubErr('');
    if (!subEmail.trim()) { setSubErr('Please enter an email address'); return; }
    try {
      setAddingSub(true);
      await inviteSubcontractor(await token(), { email: subEmail.trim(), message: subMsg.trim() });
      setShowAddSub(false); setSubEmail(''); setSubMsg(''); await load();
    } catch (e) { setSubErr(e?.response?.data?.error || 'Failed to send invitation'); }
    finally { setAddingSub(false); }
  };

  // ---------- refer ----------
  const openRefer = async () => {
    setReferErr(''); setShowRefer(true);
    try {
      const t = await token();
      const [cands, types] = await Promise.all([getReferralCandidates(t), getServiceTypes(t)]);
      setCandidates(cands); setServiceTypes(types);
    } catch (e) { console.error('[Network] refer setup failed', e); }
  };
  const onServiceChange = (value) => setRefer(r => ({ ...r, serviceType: value }));

  // When a client is picked, fetch what they have and default the selection: contact
  // + summary on, individual data fields OFF (the adviser opts in field-by-field).
  const pickUser = async (conversationId) => {
    setRefer(r => ({ ...EMPTY_REFER, conversationId, toExpertId: r.toExpertId, serviceType: r.serviceType }));
    setCandidateData(null);
    if (!conversationId) return;
    try {
      const d = await getReferralCandidateData(await token(), conversationId);
      setCandidateData(d);
      setRefer(r => ({
        ...r,
        contactFields: ['name','email','phone'].filter(k => d.contact?.[k]),
        inclSummary: !!d.hasSummary,
        inclAnalysis: false,
        fields: [],
      }));
    } catch (e) { console.error('[Network] candidate-data failed', e); }
  };

  const toggleContact = (k) => setRefer(r => ({ ...r, contactFields: r.contactFields.includes(k) ? r.contactFields.filter(x=>x!==k) : [...r.contactFields, k] }));
  const toggleField = (k) => setRefer(r => ({ ...r, fields: r.fields.includes(k) ? r.fields.filter(x=>x!==k) : [...r.fields, k] }));
  const allFieldKeys = () => (candidateData?.fields || []).map(f => f.key);
  const selectAllFields = () => setRefer(r => ({ ...r, fields: allFieldKeys() }));
  const clearFields = () => setRefer(r => ({ ...r, fields: [] }));

  // Total number of individual items the client will be asked to approve.
  const shareItemCount = refer.contactFields.length + refer.fields.length
    + (refer.inclSummary && candidateData?.hasSummary ? 1 : 0)
    + (refer.inclAnalysis && candidateData?.hasAnalysis ? 1 : 0)
    + (refer.notes.trim() ? 1 : 0);
  const doRefer = async () => {
    setReferErr('');
    if (!refer.conversationId) { setReferErr('Select a user to refer'); return; }
    if (!refer.toExpertId) { setReferErr('Select a partner adviser'); return; }
    if (!refer.serviceType) { setReferErr('Select a service'); return; }
    if (shareItemCount === 0) { setReferErr('Select at least one item to share'); return; }
    // Derive the coarse scope categories from the field-level selection.
    const scope = [];
    if (refer.contactFields.length) scope.push('contact');
    if (refer.inclSummary && candidateData?.hasSummary) scope.push('summary');
    if (refer.inclAnalysis && candidateData?.hasAnalysis) scope.push('analysis');
    if (refer.fields.length) scope.push('collectedData');
    if (refer.notes.trim()) scope.push('notes');
    try {
      setReferring(true);
      await createReferral(await token(), {
        toExpertId: refer.toExpertId, conversationId: refer.conversationId,
        serviceType: refer.serviceType, scope,
        contactFields: refer.contactFields, fields: refer.fields,
        notes: refer.notes, summary: refer.summary,
      });
      setShowRefer(false);
      setRefer(EMPTY_REFER);
      setCandidateData(null);
      setTab('referrals'); await load();
    } catch (e) { setReferErr(e?.response?.data?.error || 'Failed to create referral'); }
    finally { setReferring(false); }
  };

  const act = async (fn, ...args) => { try { await fn(await token(), ...args); await load(); } catch (e) { console.error('[Network] action failed', e); } };

  const incomingReferrals = referrals.filter(r => r.direction === 'received' && r.status === 'pending_acceptance');
  // Incoming subcontractor invites (someone asked ME to be their sub) awaiting my action.
  const incomingSubs = subLinks.filter(s => s.role === 'subcontractor' && s.status === 'pending');
  const activeSubs = subLinks.filter(s => s.status === 'active');
  const pendingSubs = subLinks.filter(s => s.status === 'pending');

  return (
    <Page>
      <Header>
        <div>
          <Title>Network</Title>
          <Subtitle>Connect with advisers you trust, then refer users to them across disciplines — with the user's consent.</Subtitle>
        </div>
        <HeaderBtns>
          <GhostBtn onClick={() => setShowInvite(true)}>+ Invite adviser</GhostBtn>
          <GhostBtn onClick={() => setShowAddSub(true)}>+ Add subcontractor</GhostBtn>
          <PrimaryBtn onClick={openRefer} disabled={activeConns.length === 0}>+ Refer a user</PrimaryBtn>
        </HeaderBtns>
      </Header>

      <Tabs>
        <Tab $active={tab==='connections'} onClick={()=>setTab('connections')}>Connections {activeConns.length>0 && <span>({activeConns.length})</span>}</Tab>
        <Tab $active={tab==='invitations'} onClick={()=>setTab('invitations')}>Invitations {invitations.length>0 && <Badge>{invitations.length}</Badge>}</Tab>
        <Tab $active={tab==='referrals'} onClick={()=>setTab('referrals')}>Referrals {incomingReferrals.length>0 && <Badge>{incomingReferrals.length}</Badge>}</Tab>
        <Tab $active={tab==='subcontractors'} onClick={()=>setTab('subcontractors')}>Subcontractors {incomingSubs.length>0 && <Badge>{incomingSubs.length}</Badge>}</Tab>
      </Tabs>

      {loading ? <Empty>Loading…</Empty> : tab === 'connections' ? (
        <>
          {activeConns.length === 0 && pendingSent.length === 0 && <Empty>No connections yet. Invite an adviser to start building your network.</Empty>}
          {activeConns.map(c => (
            <PartnerCard key={c.id} partner={c.partner}
              actions={<><StatusPill $c="#3fb950" $bg="rgba(63,185,80,.12)">Connected</StatusPill><SmallBtn $danger onClick={()=>act(revokeConnection,c.id)}>Disconnect</SmallBtn></>} />
          ))}
          {pendingSent.length > 0 && <SectionLabel>Pending — sent</SectionLabel>}
          {pendingSent.map(c => (
            <PartnerCard key={c.id} partner={c.partner}
              actions={<><StatusPill $c="#d29922" $bg="rgba(210,153,34,.12)">Pending</StatusPill><SmallBtn $danger onClick={()=>act(revokeConnection,c.id)}>Cancel</SmallBtn></>} />
          ))}
        </>
      ) : tab === 'invitations' ? (
        invitations.length === 0 ? <Empty>No pending invitations.</Empty> : invitations.map(c => (
          <PartnerCard key={c.id} partner={c.partner}
            belowEmail={c.invitationMessage && <Sub>“{c.invitationMessage}”</Sub>}
            actions={<><SmallBtn $primary onClick={()=>act(acceptConnection,c.id)}>Accept</SmallBtn><SmallBtn onClick={()=>act(declineConnection,c.id)}>Decline</SmallBtn></>} />
        ))
      ) : tab === 'referrals' ? (
        referrals.length === 0 ? <Empty>No referrals yet. Use “Refer a user” to connect one of your users with a partner.</Empty> :
        referrals.map(r => {
          const [c,bg] = STATUS_COLORS[r.status] || ['#8b98a5','rgba(139,152,165,.12)'];
          const isReceived = r.direction === 'received';
          return (
            <Card key={r.id}>
              <PartnerInfo>
                <Avatar>{initials(r.user?.name || r.counterparty?.name)}</Avatar>
                <div>
                  <Name>{r.user?.name || 'Client'} · {r.serviceLabel}</Name>
                  <Sub>{isReceived ? `From ${r.counterparty?.name}` : `To ${r.counterparty?.name}`}{r.user?.email ? ` · ${r.user.email}` : ''}</Sub>
                </div>
              </PartnerInfo>
              <Actions>
                <StatusPill $c={c} $bg={bg}>{r.statusLabel}</StatusPill>
                {isReceived && r.status === 'pending_acceptance' && (<>
                  <SmallBtn $primary onClick={()=>act(acceptReferral,r.id)}>Accept</SmallBtn>
                  <SmallBtn onClick={()=>act(declineReferral,r.id)}>Decline</SmallBtn>
                </>)}
                {isReceived && r.status === 'accepted' && <SmallBtn onClick={()=>act(updateReferralStatus,r.id,'in_progress')}>Mark in progress</SmallBtn>}
                {isReceived && (r.status === 'accepted' || r.status === 'in_progress') && (<>
                  <SmallBtn $primary onClick={()=>act(updateReferralStatus,r.id,'converted')}>Converted</SmallBtn>
                  <SmallBtn onClick={()=>act(updateReferralStatus,r.id,'closed')}>Close</SmallBtn>
                </>)}
                {!isReceived && ['pending_consent','pending_acceptance','accepted','in_progress'].includes(r.status) && <SmallBtn $danger onClick={()=>act(cancelReferral,r.id)}>Cancel</SmallBtn>}
              </Actions>
            </Card>
          );
        })
      ) : (
        subLinks.length === 0 ? <Empty>No subcontractors yet. Use “Add subcontractor” to invite someone to do work on the users you share with them.</Empty> :
        <>
          {activeSubs.map(s => (
            <PartnerCard key={s.id} partner={s.partner} fallbackName="Adviser"
              actions={<>
                <StatusPill $c="#3fb950" $bg="rgba(63,185,80,.12)">Active</StatusPill>
                <Sub>{s.role === 'contractor' ? 'My subcontractor' : 'I subcontract for them'}</Sub>
                <SmallBtn $danger onClick={()=>act(declineSubcontractor,s.id)}>Remove</SmallBtn>
              </>} />
          ))}
          {pendingSubs.length > 0 && <SectionLabel>Pending</SectionLabel>}
          {pendingSubs.map(s => (
            <PartnerCard key={s.id} partner={s.partner} fallbackName="Adviser"
              belowEmail={s.invitationMessage && <Sub>“{s.invitationMessage}”</Sub>}
              actions={<>
                <StatusPill $c="#d29922" $bg="rgba(210,153,34,.12)">Pending</StatusPill>
                {s.role === 'subcontractor' ? (<>
                  <SmallBtn $primary onClick={()=>act(acceptSubcontractor,s.id)}>Accept</SmallBtn>
                  <SmallBtn onClick={()=>act(declineSubcontractor,s.id)}>Decline</SmallBtn>
                </>) : (<>
                  <Sub>My subcontractor</Sub>
                  <SmallBtn $danger onClick={()=>act(declineSubcontractor,s.id)}>Remove</SmallBtn>
                </>)}
              </>} />
          ))}
        </>
      )}

      {/* Invite modal */}
      {showInvite && (
        <Overlay onClick={()=>!inviting && setShowInvite(false)}>
          <Modal onClick={e=>e.stopPropagation()}>
            <Title style={{fontSize:18}}>Invite an adviser</Title>
            <Subtitle style={{margin:'6px 0 0'}}>They'll get an email invite. If they're not on iMaven yet, they can join and connect.</Subtitle>
            <Label>Adviser email</Label>
            <Input type="email" value={inviteEmail} placeholder="adviser@example.com" onChange={e=>setInviteEmail(e.target.value)} />
            <Label>Message (optional)</Label>
            <Textarea value={inviteMsg} placeholder="Let's connect our services for our clients…" onChange={e=>setInviteMsg(e.target.value)} />
            {inviteErr && <ErrorMsg>{inviteErr}</ErrorMsg>}
            <ModalActions>
              <SmallBtn onClick={()=>setShowInvite(false)} disabled={inviting}>Cancel</SmallBtn>
              <PrimaryBtn onClick={doInvite} disabled={inviting}>{inviting?'Sending…':'Send invitation'}</PrimaryBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}

      {/* Add subcontractor modal */}
      {showAddSub && (
        <Overlay onClick={()=>!addingSub && setShowAddSub(false)}>
          <Modal onClick={e=>e.stopPropagation()}>
            <Title style={{fontSize:18}}>Add a subcontractor</Title>
            <Subtitle style={{margin:'6px 0 0'}}>They get their own iMaven account and see only the users you share with them. You pay for those users; they just do the work.</Subtitle>
            <Label>Subcontractor email</Label>
            <Input type="email" value={subEmail} placeholder="subcontractor@example.com" onChange={e=>setSubEmail(e.target.value)} />
            <Label>Message (optional)</Label>
            <Textarea value={subMsg} placeholder="I'd like you to help with some of my cases…" onChange={e=>setSubMsg(e.target.value)} />
            {subErr && <ErrorMsg>{subErr}</ErrorMsg>}
            <ModalActions>
              <SmallBtn onClick={()=>setShowAddSub(false)} disabled={addingSub}>Cancel</SmallBtn>
              <PrimaryBtn onClick={doAddSub} disabled={addingSub}>{addingSub?'Sending…':'Send invitation'}</PrimaryBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}

      {/* Refer modal */}
      {showRefer && (
        <Overlay onClick={()=>!referring && setShowRefer(false)}>
          <Modal onClick={e=>e.stopPropagation()}>
            <Title style={{fontSize:18}}>Refer a user to a partner</Title>
            <Subtitle style={{margin:'6px 0 0'}}>The user is asked to approve before anything is shared. Only the data you select is sent.</Subtitle>

            <Label>User</Label>
            <Select value={refer.conversationId} onChange={e=>pickUser(e.target.value)}>
              <option value="">Select a user…</option>
              {candidates.map(c => <option key={c.conversationId} value={c.conversationId}>{c.name} — {c.email}</option>)}
            </Select>

            <Label>Refer to</Label>
            <Select value={refer.toExpertId} onChange={e=>setRefer(r=>({...r,toExpertId:e.target.value}))}>
              <option value="">Select a connected adviser…</option>
              {activeConns.map(c => <option key={c.id} value={c.partner.expertId}>{c.partner.name}</option>)}
            </Select>

            <Label>Service needed</Label>
            <Select value={refer.serviceType} onChange={e=>onServiceChange(e.target.value)}>
              <option value="">Select a service…</option>
              {serviceTypes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>

            <Label>What to share {refer.conversationId && !candidateData && <em style={{fontStyle:'normal',opacity:.5,fontSize:12}}>· loading…</em>}</Label>
            {candidateData && (
              <div style={{fontSize:12, opacity:.6, margin:'-2px 0 8px'}}>Tick only what should cross over — the client approves this exact list before anything is sent.</div>
            )}

            {candidateData && (<>
              <div style={{fontSize:12, fontWeight:600, opacity:.8, margin:'6px 0 2px'}}>Contact</div>
              {['name','email','phone'].map(k => {
                const val = candidateData.contact?.[k];
                if (!val) return null;
                return (
                  <ScopeRow key={k}>
                    <input type="checkbox" checked={refer.contactFields.includes(k)} onChange={()=>toggleContact(k)} />
                    <span style={{textTransform:'capitalize'}}>{k}</span>
                    <em style={{fontStyle:'normal',opacity:.55,marginLeft:6,fontSize:12}}>· {val}</em>
                  </ScopeRow>
                );
              })}

              {candidateData.hasSummary && (
                <ScopeRow>
                  <input type="checkbox" checked={refer.inclSummary} onChange={()=>setRefer(r=>({...r,inclSummary:!r.inclSummary}))} />
                  Situation summary
                </ScopeRow>
              )}
              {candidateData.hasAnalysis && (
                <ScopeRow>
                  <input type="checkbox" checked={refer.inclAnalysis} onChange={()=>setRefer(r=>({...r,inclAnalysis:!r.inclAnalysis}))} />
                  Full AI case analysis
                  <em style={{fontStyle:'normal',opacity:.55,marginLeft:6,fontSize:12}}>· may include discipline-specific detail</em>
                </ScopeRow>
              )}

              {candidateData.fields?.length > 0 && (<>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',margin:'10px 0 2px'}}>
                  <span style={{fontSize:12, fontWeight:600, opacity:.8}}>Client data fields ({refer.fields.length}/{candidateData.fields.length})</span>
                  <span style={{fontSize:12}}>
                    <button type="button" onClick={selectAllFields} style={{background:'none',border:'none',color:'#4f9bff',cursor:'pointer',padding:0}}>Select all</button>
                    {' · '}
                    <button type="button" onClick={clearFields} style={{background:'none',border:'none',color:'#8b98a5',cursor:'pointer',padding:0}}>None</button>
                  </span>
                </div>
                <div style={{maxHeight:190, overflowY:'auto', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'4px 8px'}}>
                  {candidateData.fields.map(f => (
                    <ScopeRow key={f.key}>
                      <input type="checkbox" checked={refer.fields.includes(f.key)} onChange={()=>toggleField(f.key)} />
                      <span>{f.label}</span>
                      <em style={{fontStyle:'normal',opacity:.5,marginLeft:6,fontSize:12,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:160,display:'inline-block',verticalAlign:'bottom'}}>· {f.value}</em>
                    </ScopeRow>
                  ))}
                </div>
              </>)}
            </>)}

            <Label>Note to the partner (optional)</Label>
            <Textarea value={refer.notes} placeholder="e.g. Family of 4, arriving August, looking to rent in Toronto near schools…" onChange={e=>setRefer(r=>({...r,notes:e.target.value}))} />

            {candidateData && (
              <div style={{fontSize:12.5, opacity:.75, marginTop:10}}>
                The client will be asked to approve <strong>{shareItemCount}</strong> item{shareItemCount===1?'':'s'} before anything is shared.
              </div>
            )}
            {referErr && <ErrorMsg>{referErr}</ErrorMsg>}
            <ModalActions>
              <SmallBtn onClick={()=>setShowRefer(false)} disabled={referring}>Cancel</SmallBtn>
              <PrimaryBtn onClick={doRefer} disabled={referring}>{referring?'Sending…':`Request consent${shareItemCount?` (${shareItemCount})`:''}`}</PrimaryBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
